/* eslint-disable complexity */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallbackExperiment, useMemoExperiment } from "@million/lint/runtime";
import { Core } from "@faire/design-tokens";
import { debounceIfPossible } from "@faire/web--source/common/globals/debounceIfPossible";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Column, HorizontalLine, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { AlertBanner } from "@faire/web--source/ui/AlertBanner";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailersFilterInteraction } from "@faire/web-api--source/events/retailers/actionUnknown/filterInteraction";
import { trackRetailersFilterClearClick } from "@faire/web-api--source/events/retailers/click/filterClear";
import { trackRetailersFilterClickedClick } from "@faire/web-api--source/events/retailers/click/filterClicked";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { FilterSection } from "@faire/retailer/components/Filters/__internal__/FilterSection";
import { getFirstFilterSectionsWithTooltip, getSortedFilterSections, isDefaultShowAllFilterSection, syncToggleFilters, updateAllFilterKeys } from "@faire/retailer/components/Filters/FilterUtils";
import { ToggleFilterRow } from "@faire/retailer/components/Filters/ToggleFilterRow";
import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";
import { setTimeoutIfPossible } from "@faire/retailer-visitor-shared/lib/async-functions/setTimeoutIfPossible";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { useStickyNavContentPositions } from "@faire/retailer/navbar/useStickyNavContentPositions";
import { useSettingMillionLintFiltersOptimization } from "@faire/retailer/settings/useSettingMillionLintFiltersOptimization";
import { useRecordMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useTopOfPageContent } from "@faire/retailer-visitor-shared/stores/ui/useTopOfPageContent";
import { FilterBannerContent } from "./FilterBannerContent";
var FilterBarBase = function(param) {
    var name = param.name, _param_filterSectionState = param.filterSectionState, filterSectionState = _param_filterSectionState === void 0 ? "allExpanded" : _param_filterSectionState, className = param.className, isModal = param.isModal, headerSeparator = param.headerSeparator, _param_isSticky = param.isSticky, isSticky = _param_isSticky === void 0 ? false : _param_isSticky, isLoading = param.isLoading, PageActions = param.PageActions, shouldRetainOriginalPosition = param.shouldRetainOriginalPosition, children = param.children, _param_showUnformattedResultsCount = param.showUnformattedResultsCount, showUnformattedResultsCount = _param_showUnformattedResultsCount === void 0 ? false : _param_showUnformattedResultsCount;
    var _useFilterStore = useFilterStore([
        "filterSections",
        "activeFilterCount",
        "isSideBarVisible",
        "filterBarScrollTop",
        "preserveScrollPosition"
    ]), _useFilterStore_data = _useFilterStore.data, filterSections = _useFilterStore_data.filterSections, activeFilterCount = _useFilterStore_data.activeFilterCount, filterBarScrollTop = _useFilterStore_data.filterBarScrollTop, preserveScrollPosition = _useFilterStore_data.preserveScrollPosition, setFilterState = _useFilterStore.setFilterState, clearAllFilters = _useFilterStore.clearAllFilters;
    var shouldMemoMillionLint = useSettingMillionLintFiltersOptimization();
    var isDesktopAndAbove = useViewport().isDesktopAndAbove;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var stickyContentRef = React.useRef(null);
    var scrollContainerRef = React.useRef(null);
    var _React_useState = _sliced_to_array(React.useState(preserveScrollPosition && filterBarScrollTop !== undefined ? filterBarScrollTop : 0), 2), scrollContainerScrollTop = _React_useState[0], setScrollContainerScrollTop = _React_useState[1];
    var _useState = _sliced_to_array(useState(false), 2), sectionTooltipBannerDismissed = _useState[0], setSectionTooltipBannerDismissed = _useState[1];
    var _useTopOfPageContent = useTopOfPageContent(), topOfPageHeight = _useTopOfPageContent.heightWithoutPromoBannerExceptMarket;
    var recordMilestone = useRecordMilestone();
    var sortedFilterSections = React.useMemo(function() {
        return getSortedFilterSections(filterSections, isModal);
    }, [
        filterSections,
        isModal
    ]);
    var tooltipSectionRef = React.useRef(null);
    var sectionWithTooltip = React.useMemo(function() {
        return getFirstFilterSectionsWithTooltip(filterSections);
    }, [
        filterSections
    ]);
    var tooltip = isModal ? undefined : sectionWithTooltip === null || sectionWithTooltip === void 0 ? void 0 : sectionWithTooltip.tooltip;
    var tooltipRef = useRef(tooltip);
    tooltipRef.current = tooltip;
    var updateFilterBarScrollTop = useCallback(function() {
        setFilterState(function(currentState) {
            var _scrollContainerRef_current;
            var _scrollContainerRef_current_scrollTop;
            return _object_spread_props(_object_spread({}, currentState), {
                filterBarScrollTop: (_scrollContainerRef_current_scrollTop = (_scrollContainerRef_current = scrollContainerRef.current) === null || _scrollContainerRef_current === void 0 ? void 0 : _scrollContainerRef_current.scrollTop) !== null && _scrollContainerRef_current_scrollTop !== void 0 ? _scrollContainerRef_current_scrollTop : 0
            });
        });
    }, [
        setFilterState
    ]);
    var debouncedUpdateFilterBarScrollTop = useMemo(function() {
        return debounceIfPossible(updateFilterBarScrollTop, 500);
    }, [
        updateFilterBarScrollTop
    ]);
    var hasMounted = useRef(false);
    useLayoutEffect(function() {
        if (preserveScrollPosition && !hasMounted.current && scrollContainerRef.current) {
            var _scrollContainerRef_current_scrollTo, _scrollContainerRef_current;
            hasMounted.current = true;
            (_scrollContainerRef_current = scrollContainerRef.current) === null || _scrollContainerRef_current === void 0 ? void 0 : (_scrollContainerRef_current_scrollTo = _scrollContainerRef_current.scrollTo) === null || _scrollContainerRef_current_scrollTo === void 0 ? void 0 : _scrollContainerRef_current_scrollTo.call(_scrollContainerRef_current, {
                top: filterBarScrollTop
            });
        }
    }, [
        preserveScrollPosition,
        filterBarScrollTop
    ]);
    var handleFilterSelected = useCallbackExperiment(function(section, filterKey, value) {
        trackRetailersFilterClickedClick(name, filterKey);
        var tooltip = tooltipRef.current;
        if (shouldMemoMillionLint) {
            setFilterState(function(currentState) {
                var sectionsUpdated = updateAllFilterKeys(currentState.filterSections, filterKey, value);
                syncToggleFilters(section, sectionsUpdated);
                return _object_spread_props(_object_spread({}, currentState), {
                    filterSections: _to_consumable_array(sectionsUpdated),
                    isNewFilterSelected: true
                });
            });
            if (section.tooltip === tooltip) {
                setSectionTooltipBannerDismissed(true);
            }
        } else {
            var sectionsUpdated = updateAllFilterKeys(filterSections, filterKey, value);
            syncToggleFilters(section, sectionsUpdated);
            setFilterState(function(currentState) {
                return _object_spread_props(_object_spread({}, currentState), {
                    filterSections: _to_consumable_array(sectionsUpdated),
                    isNewFilterSelected: true
                });
            });
            if (section.tooltip === tooltip) {
                setSectionTooltipBannerDismissed(true);
            }
        }
    }, [
        name,
        setFilterState
    ], shouldMemoMillionLint);
    var handleMilestone = useCallbackExperiment(function(milestone) {
        if (!milestone || !milestone.retailer_milestone) {
            return;
        }
        recordMilestone(milestone.retailer_milestone);
    }, [
        recordMilestone
    ], shouldMemoMillionLint);
    var handleCloseTooltip = useCallbackExperiment(function() {
        handleMilestone(tooltip === null || tooltip === void 0 ? void 0 : tooltip.dismiss_milestone);
        setSectionTooltipBannerDismissed(true);
    }, [
        handleMilestone,
        tooltip === null || tooltip === void 0 ? void 0 : tooltip.dismiss_milestone
    ], shouldMemoMillionLint);
    var handleScrollToSection = useCallbackExperiment(function() {
        var _tooltipSectionRef_current, _tooltipSectionRef_current1, _scrollContainerRef_current_scrollTo, _scrollContainerRef_current;
        var top = (tooltipSectionRef === null || tooltipSectionRef === void 0 ? void 0 : (_tooltipSectionRef_current = tooltipSectionRef.current) === null || _tooltipSectionRef_current === void 0 ? void 0 : _tooltipSectionRef_current.offsetTop) ? (tooltipSectionRef === null || tooltipSectionRef === void 0 ? void 0 : (_tooltipSectionRef_current1 = tooltipSectionRef.current) === null || _tooltipSectionRef_current1 === void 0 ? void 0 : _tooltipSectionRef_current1.offsetTop) - 16 : 0;
        scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : (_scrollContainerRef_current = scrollContainerRef.current) === null || _scrollContainerRef_current === void 0 ? void 0 : (_scrollContainerRef_current_scrollTo = _scrollContainerRef_current.scrollTo) === null || _scrollContainerRef_current_scrollTo === void 0 ? void 0 : _scrollContainerRef_current_scrollTo.call(_scrollContainerRef_current, {
            top: top,
            behavior: "smooth"
        });
        if (preserveScrollPosition) {
            debouncedUpdateFilterBarScrollTop();
        }
        setTimeoutIfPossible(handleCloseTooltip, 2000);
    }, [
        handleCloseTooltip,
        preserveScrollPosition
    ], shouldMemoMillionLint);
    var isSSR = useIsSSR();
    var handleClearFilters = useCallbackExperiment(function() {
        if (activeFilterCount > 0) {
            var _scrollContainerRef_current_scrollTo, _scrollContainerRef_current;
            var actionName = isModal ? "modal_clear_all" : "sidebar_clear_all";
            trackRetailersFilterClearClick(name, actionName, "");
            clearAllFilters();
            (_scrollContainerRef_current = scrollContainerRef.current) === null || _scrollContainerRef_current === void 0 ? void 0 : (_scrollContainerRef_current_scrollTo = _scrollContainerRef_current.scrollTo) === null || _scrollContainerRef_current_scrollTo === void 0 ? void 0 : _scrollContainerRef_current_scrollTo.call(_scrollContainerRef_current, {
                top: 0,
                behavior: "smooth"
            });
            if (preserveScrollPosition) {
                debouncedUpdateFilterBarScrollTop();
            }
        }
    }, [
        activeFilterCount,
        clearAllFilters,
        isModal,
        name,
        preserveScrollPosition
    ], shouldMemoMillionLint);
    var viewRef = useDoActionWhenVisible(function() {
        var _sectionWithTooltip_field_name;
        trackRetailersFilterInteraction(name, "tooltip_view", (_sectionWithTooltip_field_name = sectionWithTooltip === null || sectionWithTooltip === void 0 ? void 0 : sectionWithTooltip.field_name) !== null && _sectionWithTooltip_field_name !== void 0 ? _sectionWithTooltip_field_name : "");
        handleMilestone(tooltip === null || tooltip === void 0 ? void 0 : tooltip.seen_milestone);
    });
    var header = /*#__PURE__*/ _jsxs(ColumnWithPseudoElement, {
        ref: stickyContentRef,
        children: [
            /*#__PURE__*/ _jsxs(HeaderContainer, {
                role: "heading",
                "aria-level": 2,
                $useUpdatedSpacing: isLoggedInRetailer(),
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        "data-test-id": "filter-bar-title",
                        variant: isLoggedInRetailer() ? "paragraphSansMedium" : "subheadingSansMedium",
                        children: activeFilterCount > 0 ? /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "00q10I",
                            defaultMessage: [
                                {
                                    type: 6,
                                    value: "activeFilterNumber",
                                    options: {
                                        one: {
                                            value: [
                                                {
                                                    type: 7
                                                },
                                                {
                                                    type: 0,
                                                    value: " filter applied"
                                                }
                                            ]
                                        },
                                        other: {
                                            value: [
                                                {
                                                    type: 7
                                                },
                                                {
                                                    type: 0,
                                                    value: " filters applied"
                                                }
                                            ]
                                        }
                                    },
                                    offset: 0,
                                    pluralType: "cardinal"
                                }
                            ],
                            values: {
                                activeFilterNumber: activeFilterCount
                            }
                        }) : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "xUfZ3f",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Filters"
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(ClearAllColumn, {
                        $useUpdatedSpacing: isLoggedInRetailer(),
                        children: activeFilterCount > 0 ? /*#__PURE__*/ _jsx(Button, {
                            variant: "tertiary",
                            onClick: handleClearFilters,
                            "data-test-id": "filter-sidebar-clean-all",
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "M+I5uz",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Clear all"
                                    }
                                ]
                            })
                        }) : null
                    })
                ]
            }),
            headerSeparator || scrollContainerScrollTop > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: isModal ? "3x" : "2x"
                    }),
                    /*#__PURE__*/ _jsx(HorizontalLine, {})
                ]
            }) : null
        ]
    });
    var filteredSectionsMemoed = useMemoExperiment(function() {
        return sortedFilterSections.length === 0 && isLoading ? Array.from({
            length: 6
        }, function(_value, index) {
            return /*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(FilterSection, {
                        name: name,
                        section: IFilterSection.build(),
                        onChange: handleFilterSelected,
                        initiallyCollapsed: true,
                        showLoading: true
                    }),
                    /*#__PURE__*/ _jsx(HorizontalLine, {})
                ]
            }, "loadingPlaceholder_".concat(index));
        }) : sortedFilterSections.map(function(filterSection, index) {
            if (filterSection.selection_type === IFilterSection.SelectionType.TOGGLE) {
                return null;
            }
            var collapsed = filterSectionState === "allCollapsed";
            var showAll = isDefaultShowAllFilterSection(filterSection);
            return /*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(FilterSection, {
                        name: name,
                        section: filterSection,
                        onChange: handleFilterSelected,
                        initiallyCollapsed: collapsed,
                        showAll: showAll,
                        divRef: tooltip === filterSection.tooltip ? tooltipSectionRef : undefined,
                        showUnformattedResultsCount: showUnformattedResultsCount
                    }),
                    /*#__PURE__*/ _jsx(HorizontalLine, {})
                ]
            }, "".concat(filterSection.display_name, "-").concat(index));
        });
    }, [
        filterSectionState,
        handleFilterSelected,
        isLoading,
        name,
        showUnformattedResultsCount,
        sortedFilterSections,
        tooltip
    ], shouldMemoMillionLint);
    if (sortedFilterSections.length === 0 && !isLoading && isModal) {
        return /*#__PURE__*/ _jsx(Wrapper, {
            isModal: isModal,
            align: "center",
            justify: "center",
            children: /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraphSansMedium",
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "MlJYnk",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Your search did not match any products"
                        }
                    ]
                })
            })
        });
    }
    if (isSSR) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Wrapper, {
        isModal: isModal,
        className: className,
        "data-test-id": "filter-bar",
        style: isSticky ? {
            height: "calc(100vh - " + (topOfPageHeight + 16) + "px)",
            position: "sticky",
            top: isLoggedInRetailer() && isDesktopAndAbove && !shouldRetainOriginalPosition ? 16 : topOfPageHeight + 16 + "px"
        } : undefined,
        onWheel: function(e) {
            // preventing the window scroll event from triggering
            e.stopPropagation();
        },
        children: [
            header,
            /*#__PURE__*/ _jsxs(ScrollContainer, {
                ref: scrollContainerRef,
                onScroll: function(e) {
                    if (scrollContainerScrollTop === 0 && e.currentTarget.scrollTop !== 0 || scrollContainerScrollTop !== 0 && e.currentTarget.scrollTop === 0) {
                        setScrollContainerScrollTop(e.currentTarget.scrollTop);
                    }
                    if (preserveScrollPosition) {
                        debouncedUpdateFilterBarScrollTop();
                    }
                },
                children: [
                    !sectionTooltipBannerDismissed && tooltip && tooltip.message ? /*#__PURE__*/ _jsx("div", {
                        ref: viewRef,
                        children: /*#__PURE__*/ _jsx(StyledAlertBanner, {
                            message: /*#__PURE__*/ _jsx(FilterBannerContent, {
                                title: tooltip.title,
                                message: tooltip.message,
                                ctaText: strictLocalize({
                                    id: "aDtQDs",
                                    defaultMessage: [
                                        {
                                            type: 0,
                                            value: "Apply filter"
                                        }
                                    ]
                                }),
                                ctaOnClick: handleScrollToSection
                            }),
                            onClose: handleCloseTooltip
                        })
                    }) : null,
                    isModal ? /*#__PURE__*/ _jsx(StyledToggleFilterRow, {
                        name: name,
                        wrap: true
                    }) : null,
                    children,
                    filteredSectionsMemoed,
                    PageActions,
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: {
                            mobileAndAbove: 0,
                            desktopAndAbove: "9x"
                        }
                    })
                ]
            })
        ]
    });
};
export var FilterBar = function(props) {
    var shouldRetainOriginalPosition = useStickyNavContentPositions().shouldRetainOriginalPosition;
    return /*#__PURE__*/ _jsx(MemoizedFilterBarBase, _object_spread({
        shouldRetainOriginalPosition: shouldRetainOriginalPosition
    }, props));
};
var MemoizedFilterBarBase = /*#__PURE__*/ React.memo(FilterBarBase);
var Wrapper = styled(Column).withConfig({
    componentId: "sc-e0790239-0"
})([
    "z-index:4;width:100%;",
    "{height:100%;}",
    "{",
    ";}",
    "{",
    ";",
    "}",
    "{",
    "}"
], responsiveMediaQueries.tabletAndBelow, responsiveMediaQueries.desktopAndAbove, function() {
    return isLoggedInRetailer() ? "transition: top 300ms cubic-bezier(0.175, 0.735, 0.680, 0.875);" : undefined;
}, responsiveMediaQueries.tabletAndBelow, function() {
    return isLoggedInRetailer() ? "transition: top 300ms cubic-bezier(0.175, 0.735, 0.680, 0.875);" : undefined;
}, function(param) {
    var isModal = param.isModal;
    return isModal ? css([
        "overflow:auto;padding:",
        ";margin-bottom:",
        ";"
    ], getSpacing(0, 12), getSpacing("6x")) : undefined;
}, responsiveMediaQueries.mobile, function(param) {
    var isModal = param.isModal;
    return isModal ? css([
        "margin-bottom:",
        ";"
    ], getSpacing("2x")) : undefined;
});
// eslint-disable-next-line @faire/prefer-slate-attrs -- move align to a responsive prop
var HeaderContainer = styled(Row).withConfig({
    componentId: "sc-e0790239-1"
})([
    "padding-right:3px;",
    "{padding:",
    ";height:",
    ";align-items:",
    ";}"
], responsiveMediaQueries.desktopAndAbove, function(param) {
    var $useUpdatedSpacing = param.$useUpdatedSpacing;
    return $useUpdatedSpacing ? getSpacing("2x", 0) : undefined;
}, function(param) {
    var $useUpdatedSpacing = param.$useUpdatedSpacing;
    return $useUpdatedSpacing ? "40px" : undefined;
}, function(param) {
    var $useUpdatedSpacing = param.$useUpdatedSpacing;
    return $useUpdatedSpacing ? "center" : undefined;
});
var ColumnWithPseudoElement = styled(Column).withConfig({
    componentId: "sc-e0790239-2"
})([
    "background:",
    ';&::after{content:"";position:absolute;top:-16px;left:0;width:100%;height:16px;background:',
    ";}"
], Core.surface.inverse, Core.surface.inverse);
var ScrollContainer = styled.div.withConfig({
    componentId: "sc-e0790239-3"
})([
    "height:100%;overflow-y:auto;overflow-x:visible;scrollbar-width:thin;scrollbar-color:",
    " ",
    ";&::-webkit-scrollbar{width:3px;background-color:",
    ";}&::-webkit-scrollbar-track{background-color:",
    ";}&::-webkit-scrollbar-thumb{border-radius:10px;background-color:",
    ";}"
], Core.surface.subdued, Core.surface.inverse, Core.surface.inverse, Core.surface.inverse, Core.surface.subdued);
var ClearAllColumn = styled(Column).withConfig({
    componentId: "sc-e0790239-4"
})([
    "margin:",
    ";margin-left:auto;margin-right:",
    ";"
], function(param) {
    var $useUpdatedSpacing = param.$useUpdatedSpacing;
    return $useUpdatedSpacing ? undefined : "auto";
}, getSpacing("2x"));
var StyledToggleFilterRow = styled(ToggleFilterRow).withConfig({
    componentId: "sc-e0790239-5"
})([
    "row-gap:",
    ";padding-top:",
    ";padding-bottom:",
    ";border-bottom:1px solid ",
    ";"
], getSpacing("1x"), getSpacing("3x"), getSpacing("3x"), Core.border.muted);
var StyledAlertBanner = styled(AlertBanner).withConfig({
    componentId: "sc-e0790239-6"
})([
    "background-color:",
    ";align-items:baseline;width:calc(100% - 16px);margin-top:",
    ";margin-bottom:",
    ";"
], Core.surface.subdued, getSpacing("3x"), getSpacing("1x"));
